import React from 'react';
import { 
  Text, 
  Table, 
  TableBody, 
  TableRow, 
  TableCell,
  TableHeader,
  TableHeaderCell,
  Button
} from "@fluentui/react-components";
import { useTranslation } from 'react-i18next';
import { Dismiss20Filled } from '@fluentui/react-icons';
import { Attachment } from '../../../../../../contexts/SettingsContext';
import { csvToRows } from '../../../../../../utils/RangeUtils';

interface AttachmentTableProps {
  attachment: Attachment | string;
  tableIndex: number;
  styles: any;
  onRemove: () => void
}

const AttachmentTable: React.FC<AttachmentTableProps> = ({ 
  attachment, 
  tableIndex, 
  styles,
  onRemove
}) => {
  const { t } = useTranslation();

  const isStructured = typeof attachment !== 'string';
  const headers = isStructured ? attachment.headers : null;
  const columnLetters = isStructured ? attachment.columnLetters : null;
  const rows = isStructured ? csvToRows(attachment.dataCsv) : csvToRows(attachment);
  if (rows.length === 0) return null;
  
  const columnCount = headers ? headers.length : Math.max(...rows.map(row => row.length));
  const rowCount = rows.length;

  const headerLabel = (index: number): string => {
    const letter = columnLetters && columnLetters[index] ? columnLetters[index] : '';
    const name = headers && headers[index] !== undefined && headers[index] !== ''
      ? headers[index]
      : t("chat.column", { number: index + 1 });
    return letter ? `${letter} — ${name}` : name;
  };

  return (
    <div className={styles.tableAttachment}>
        <div className={styles.tableHeader}>
            <Text size={100} weight="semibold">
            {t("chat.tableInfo", {
                index: tableIndex + 1,
                rows: rowCount,
                columns: columnCount
            })}
            </Text>
            <Button
                size="small"
                appearance="subtle"
                icon={<Dismiss20Filled />}
                onClick={onRemove}
                className={styles.removeAttachmentButton}
            />
        </div>
      
      <div className={styles.tableWrapper}>
        <div className={styles.tableScrollContainer}>
          <Table size="extra-small" className={styles.dataTable}>
            <TableHeader>
              <TableRow>
                {Array.from({ length: columnCount }, (_, i) => (
                  <TableHeaderCell key={i} className={styles.tableHeaderCell}>
                    <Text size={100} weight="semibold" truncate>
                      {headerLabel(i)}
                    </Text>
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <TableCell key={cellIndex} className={styles.tableCell}>
                      <Text 
                        size={100} 
                        style={{ fontFamily: 'monospace', fontSize: '11px' }}
                        truncate
                      >
                        {cell === '' ? ' ' : cell}
                      </Text>
                    </TableCell>
                  ))}
                  {/* Fill empty cells if row has fewer columns */}
                  {row.length < columnCount && 
                    Array.from({ length: columnCount - row.length }).map((_, i) => (
                      <TableCell key={`empty-${i}`} className={styles.tableCell}>
                        <Text size={100}> </Text>
                      </TableCell>
                    ))
                  }
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default AttachmentTable;